import { useEffect, useMemo, useState } from 'react';

const STORAGE_KEY_PREFIX = 'notificationActiveTab';
const CHANNEL_NAME_PREFIX = 'notification-tab';

const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

function generateTabId() {
  if (isBrowser && 'crypto' in window && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useNotificationTabCoordinator(role = 'agent') {
  const storageKey = `${STORAGE_KEY_PREFIX}_${role}`;
  const channelName = `${CHANNEL_NAME_PREFIX}_${role}`;
  const tabId = useMemo(() => generateTabId(), []);
  const [isNotificationOwner, setIsNotificationOwner] = useState(false);

  useEffect(() => {
    if (!isBrowser) return undefined;

    let channel;
    try {
      if ('BroadcastChannel' in window) {
        channel = new BroadcastChannel(channelName);
      }
    } catch (err) {
      channel = null;
    }

    const claimOwnership = () => {
      try {
        localStorage.setItem(storageKey, tabId);
      } catch (err) {
        console.warn(`Notification tab coordinator (${role}): failed to write localStorage`, err);
      }
      setIsNotificationOwner(true);
      if (channel) {
        channel.postMessage({ type: `active-${role}-notification-tab`, tabId });
      }
    };

    const updateOwnershipFromStorage = () => {
      try {
        const activeTabId = localStorage.getItem(storageKey);
        setIsNotificationOwner(activeTabId === tabId);
      } catch (err) {
        console.warn(`Notification tab coordinator (${role}): failed to read localStorage`, err);
      }
    };

    const handleStorage = (event) => {
      if (event.key !== storageKey) return;
      setIsNotificationOwner(event.newValue === tabId);
    };

    const handleChannel = (event) => {
      if (!event?.data || event.data.type !== `active-${role}-notification-tab`) return;
      setIsNotificationOwner(event.data.tabId === tabId);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        claimOwnership();
      }
    };

    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (channel) {
      channel.addEventListener('message', handleChannel);
    }

    claimOwnership();
    updateOwnershipFromStorage();

    return () => {
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (channel) {
        channel.removeEventListener('message', handleChannel);
      }
      try {
        if (localStorage.getItem(storageKey) === tabId) {
          localStorage.removeItem(storageKey);
        }
      } catch (err) {
        console.warn(`Notification tab coordinator (${role}): cleanup failed`, err);
      }
    };
  }, [tabId, role, storageKey, channelName]);

  return { isNotificationOwner };
}
